import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "components/index";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
} from "@mui/material";

const UsersFilter = observer(({ updateFilters }) => {
  const [filters, setFilters] = useState({
    orgUnit: "all",
    status: "all",
    accountType: "all",
    search: "",
  });
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [orgUnits, setOrgUnits] = useState([]);

  useEffect(() => {
    (async () => {
      const { default: SuittService } = await import(
        "src/services/SuittService"
      );
      const service = new SuittService();
      const res = await service.getOrgUnits();
      if (res?.status && Array.isArray(res.data)) {
        const list = res.data;
        const byParent = new Map();
        for (const u of list) {
          const key = u.parentCode ?? "__ROOT__";
          const arr = byParent.get(key) || [];
          arr.push(u);
          byParent.set(key, arr);
        }
        const ordered = [];
        const walk = (parentCode, depth) => {
          const key = parentCode ?? "__ROOT__";
          const arr = (byParent.get(key) || []).sort((a, b) =>
            a.nameUk.localeCompare(b.nameUk)
          );
          for (const u of arr) {
            ordered.push({
              ...u,
              _depth: depth,
              _indentLabel: `${"— ".repeat(depth)}${u.nameUk}`,
            });
            walk(u.code, depth + 1);
          }
        };
        walk(null, 0);
        setOrgUnits(ordered);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isInitialRender) {
      updateFilters(filters);
    } else {
      setIsInitialRender(false);
    }
  }, [filters, isInitialRender, updateFilters]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <TextField
            fullWidth
            size={"small"}
            label={"Пошук"}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl fullWidth>
            <Autocomplete
              options={[
                { code: "all", nameUk: "Всі", _indentLabel: "Всі" },
                ...orgUnits,
              ]}
              getOptionLabel={(o) => o._indentLabel || o.nameUk || ""}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              size={"small"}
              value={[
                { code: "all", nameUk: "Всі", _indentLabel: "Всі" },
                ...orgUnits,
              ].find((o) => o.code === filters.orgUnit)}
              onChange={(e, val) => {
                const orgUnit = val?.code || "all";
                setFilters({ ...filters, orgUnit });
              }}
              renderInput={(params) => (
                <TextField {...params} label={"Підрозділ"} size="small" />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl fullWidth>
            <InputLabel id="label-status">Статус</InputLabel>
            <Select
              labelId="label-status"
              id="select-status"
              label={"Статус"}
              size={"small"}
              value={filters.status}
              onChange={(e) => {
                const status = e.target.value;
                setFilters({ ...filters, status });
              }}
            >
              <MenuItem value={"all"}>{"Всі"}</MenuItem>
              <MenuItem value={"active"}>{"Активний"}</MenuItem>
              <MenuItem value={"inactive"}>{"Неактивний"}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <FormControl fullWidth>
            <InputLabel id="label-role">Роль</InputLabel>
            <Select
              labelId="label-role"
              id="select-role"
              label={"Роль"}
              size={"small"}
              value={filters.accountType}
              onChange={(e) => {
                const accountType = e.target.value;
                setFilters({ ...filters, accountType });
              }}
            >
              <MenuItem value={"all"}>{"Всі"}</MenuItem>
              <MenuItem value={"HR"}>{"HR"}</MenuItem>
              <MenuItem value={"Teacher"}>{"Teacher"}</MenuItem>
              <MenuItem value={"ViceRectorForScientificWork"}>
                {"ViceRectorForScientificWork"}
              </MenuItem>
              <MenuItem value={"ViceRectorForEducationalWork"}>
                {"ViceRectorForEducationalWork"}
              </MenuItem>
              <MenuItem value={"DeanOfTheFaculty"}>
                {"DeanOfTheFaculty"}
              </MenuItem>
              <MenuItem value={"HeadOfTheDepartment"}>
                {"HeadOfTheDepartment"}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
});

export default UsersFilter;
